import { useEffect, useMemo, useState } from 'react'
import { isNativeContainer } from '../pwa/nativeContainer'

export type InstallPlatform = 'android' | 'ios' | 'other'

/**
 * `BeforeInstallPromptEvent` is Chromium-only and not in `lib.dom.d.ts`.
 * See webapp-homescreen.md.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function detectPlatform(): InstallPlatform {
  const ua = navigator.userAgent
  if (/android/i.test(ua)) return 'android'
  // iPadOS 13+ reports as Mac Safari with touch support; the touch check disambiguates it
  // from a real desktop Mac (webapp-homescreen.md's iOS Share-sheet path applies to both).
  if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'ios'
  }
  return 'other'
}

/**
 * `null` where `matchMedia` is missing — a host that has none (a test renderer, an embedded
 * webview) still has `navigator.standalone` to answer with, and a display-mode query is the wrong
 * thing to take a whole app bar down for. This became load-bearing when `AppBar` started reading
 * the hook for its dot: the shell's most-rendered component must not require a browser API that
 * only tells it which of two shades of "not installed" it is in.
 */
function standaloneMedia(): MediaQueryList | null {
  if (typeof window.matchMedia !== 'function') return null
  return window.matchMedia('(display-mode: standalone)')
}

function isStandaloneDisplay(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean }
  return standaloneMedia()?.matches === true || nav.standalone === true
}

/**
 * D30/EC-14's installability entry point (PRD §7). Android exposes a native prompt through
 * `beforeinstallprompt`; iOS has no programmatic install and needs the Share-sheet
 * instruction path (`InstallPromptSheet`). Installed/dismissed state is intentionally not
 * tracked here — device-cache.md: installation is browser/OS state, never an SeloGate
 * record, so there is no re-prompt schedule to persist.
 */
export function useInstallPrompt(): {
  readonly platform: InstallPlatform
  readonly isStandalone: boolean
  readonly canPromptNatively: boolean
  readonly canInstall: boolean
  promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'>
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [isStandalone, setIsStandalone] = useState(isStandaloneDisplay)
  const platform = useMemo(detectPlatform, [])

  useEffect(() => {
    function onBeforeInstallPrompt(event: Event): void {
      event.preventDefault()
      setDeferred(event as BeforeInstallPromptEvent)
    }
    function onInstalled(): void {
      setDeferred(null)
      setIsStandalone(true)
    }
    const media = standaloneMedia()
    function onDisplayModeChange(): void {
      setIsStandalone(isStandaloneDisplay())
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onInstalled)
    media?.addEventListener('change', onDisplayModeChange)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onInstalled)
      media?.removeEventListener('change', onDisplayModeChange)
    }
  }, [])

  async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (deferred === null) return 'unavailable'
    await deferred.prompt()
    const choice = await deferred.userChoice
    setDeferred(null)
    return choice.outcome
  }

  /**
   * **The one predicate every piece of install chrome is allowed to ask.**
   *
   * `!isStandalone` alone was the old test, and it is not the same question: a desktop browser
   * with no `beforeinstallprompt` is not installed *and* cannot be, so a card, a red dot, or a
   * row offering it points at nothing that can happen. The two ways to install are the OS prompt
   * (Chromium, once the event has fired) and iOS's Share sheet (`InstallPromptSheet`) — there is
   * no third, and `platform === 'other'` without the event has neither.
   *
   * **The native container is a third way of having no way**, and it is not `isStandalone`. Inside
   * a WKWebView the UA says `iPhone`, the display-mode query is false, and `navigator.standalone`
   * is `undefined` (it is Safari-only) — so without this term the iOS app opened on an
   * add-to-home-screen card offering to install an app that was already installed. See
   * `pwa/nativeContainer.ts` for why that is asked as its own question rather than by widening
   * `isStandalone`, and why Android never showed it.
   */
  const canInstall = !isNativeContainer() && !isStandalone && (platform === 'ios' || deferred !== null)

  return { platform, isStandalone, canPromptNatively: deferred !== null, canInstall, promptInstall }
}
